<template>
	<div
		:class="toastClasses"
		role="alert"
		aria-live="assertive"
		aria-atomic="true"
		@click="$emit('dismiss')"
	>
		<div
			v-if="resolvedIconComponent"
			:class="['mr-3 shrink-0', iconColorClass]"
		>
			<Component :is="resolvedIconComponent" size="20" />
		</div>

		<div
			v-else-if="resolvedLegacyIcon"
			:class="['mr-3 shrink-0', iconColorClass]"
		>
			<Icon :icon="resolvedLegacyIcon" />
		</div>

		<div class="min-w-0 flex-1">
			<h3 v-if="title" :class="titleClasses">
				{{ title }}
			</h3>

			<div v-if="$slots.default || message" :class="bodyClasses">
				<slot>
					<ul v-if="Array.isArray(message)" class="p-0 m-0 list-none">
						<li v-for="(m, i) in message" :key="i">
							{{ m }}
						</li>
					</ul>

					<div v-else>
						{{ message }}
					</div>
				</slot>
			</div>
		</div>
	</div>
</template>

<script>
import {
	IconAlertTriangle,
	IconCheck,
	IconInfoCircle,
	IconTimesCircle
} from '~/components/Icons'
import Icon from '~/components/ui/common/Icon'

export default {
	components: {
		IconAlertTriangle,
		IconCheck,
		IconInfoCircle,
		IconTimesCircle,
		Icon
	},
	props: {
		title: {
			type: String,
			default: null
		},
		message: {
			type: [String, Array],
			default: null
		},
		type: {
			type: String,
			default: null
		},
		icon: {
			type: [String, Array, Object, Function],
			default: null
		},
		theme: {
			type: String,
			default: 'dark'
		},
		persist: {
			type: Boolean,
			default: false
		},
		lifetime: {
			type: Number,
			default: 5000
		}
	},
	data() {
		return {
			dismissTimeoutId: null
		}
	},
	computed: {
		defaultIconComponent() {
			if (this.type === 'success') return IconCheck
			if (this.type === 'info') return IconInfoCircle
			if (this.type === 'warning') return IconAlertTriangle
			if (this.type === 'danger') return IconTimesCircle
			return null
		},
		normalizedTheme() {
			return this.theme === 'light' ? 'light' : 'dark'
		},
		isLegacyIconDefinition() {
			return (
				typeof this.icon === 'string' ||
				Array.isArray(this.icon) ||
				Boolean(this.icon && this.icon.iconName)
			)
		},
		resolvedIconComponent() {
			if (this.icon) {
				return this.isLegacyIconDefinition ? null : this.icon
			}

			return this.defaultIconComponent
		},
		resolvedLegacyIcon() {
			return this.isLegacyIconDefinition ? this.icon : null
		},
		toastClasses() {
			return [
				'relative flex rounded-lg px-4 py-4 shadow-xl',
				this.normalizedTheme === 'light'
					? 'bg-white text-neutral-700'
					: 'bg-grey-950 text-white',
				!this.$slots.default && !this.message ? 'items-center' : null
			]
		},
		titleClasses() {
			return [
				'text-base font-bold',
				this.$slots.default || this.message ? 'mb-1' : 'mb-0',
				this.normalizedTheme === 'light'
					? 'text-neutral-700'
					: 'text-white'
			]
		},
		bodyClasses() {
			return 'text-sm leading-5'
		},
		iconColorClass() {
			return this.normalizedTheme === 'light'
				? 'text-neutral-700'
				: 'text-white'
		}
	},
	mounted() {
		if (!this.persist) {
			this.dismissTimeoutId = setTimeout(() => {
				this.dismissTimeoutId = null
				this.$emit('dismiss')
			}, this.lifetime)
		}
	},
	beforeDestroy() {
		if (this.dismissTimeoutId) {
			clearTimeout(this.dismissTimeoutId)
			this.dismissTimeoutId = null
		}
	}
}
</script>
